import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import React, {useEffect, useMemo, useState} from 'react';
import {useWatch} from 'react-hook-form';
import type {
  InspectionRequest,
  InspectionResponse,
} from 'src/api/ProactApiClient';
import {InfoAlert} from 'src/design/molecules/InfoAlert';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {BackBar} from 'src/app/layout/BackBar';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useEntitySyncedForm} from 'src/app/forms/engine/useEntitySyncedForm';
import {SidebarLinkInspections} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {
  InspectionCreateFormMeta,
  InspectionDocumentTitle,
  InspectionEditFormMeta,
  isInspectionEditableState,
  MapInspectionResponseToInspectionRequest,
} from 'src/pages/Modules/Inspections/shared/inspectionHelpers';
import {
  useCreateInspectionMutation,
  useGetInspectionAssigneeCandidatesQuery,
  useGetInspectionQuery,
  useUpdateInspectionMutation,
} from 'src/redux/queries/inspection';
import {useGetInspectionTypeSelectOptionsQuery} from 'src/redux/queries/inspectionType';
import {
  useGetSiteQuery,
  useGetSiteSelectOptionsQuery,
} from 'src/redux/queries/site';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useInspectionBreadcrumbs} from 'src/shared/hooks/useInspectionBreadcrumbs';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormFieldV2} from 'src/types/FormV2';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const EMPTY_SELECT_QUERY = {
  start: 0,
  length: 10000,
  filters: [],
  filterFns: [],
  sorting: [],
};

export interface InspectionFormProps {
  id?: number;
  mode: 'create' | 'edit';
}

export const InspectionForm: React.FC<InspectionFormProps> = ({id, mode}) => {
  useSetDocumentTitle(InspectionDocumentTitle);
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();
  const [isSavePending, setIsSavePending] = useState(false);

  const isCreateMode = mode === 'create';
  const isEditMode = mode === 'edit';

  const {data: sites, isLoading: isSitesLoading} =
    useGetSiteSelectOptionsQuery(EMPTY_SELECT_QUERY);
  const {data: inspectionTypes, isLoading: isTypesLoading} =
    useGetInspectionTypeSelectOptionsQuery(EMPTY_SELECT_QUERY);
  const {data: assigneeCandidates, isLoading: isCandidatesLoading} =
    useGetInspectionAssigneeCandidatesQuery(undefined, {skip: isEditMode});

  const {data: fetchedInspection, isLoading: isInspectionLoading} =
    useGetInspectionQuery(id!, {skip: !isEditMode || id === undefined});

  const [createInspection] = useCreateInspectionMutation();
  const [updateInspection] = useUpdateInspectionMutation();

  const item = fetchedInspection;
  const isOptionsLoading =
    isSitesLoading || isTypesLoading || (isCreateMode && isCandidatesLoading);
  const isLoading = isEditMode && (isInspectionLoading || !item);

  const {breadcrumbs, fallbackTo, fallbackLabel} = useInspectionBreadcrumbs({
    mode: isEditMode ? 'edit' : 'create',
    name: item?.siteName,
    inspectionId: item?.id,
  });

  useEffect(() => {
    if (isEditMode && item && !isInspectionEditableState(item.state)) {
      navigate(PathUtility.InspectionsView(item.id), {
        replace: true,
      });
    }
  }, [isEditMode, item, navigate]);

  const formMeta = useMemo<IFormFieldV2[]>(() => {
    if (isOptionsLoading) return [];
    return isEditMode
      ? InspectionEditFormMeta(sites?.data ?? [], inspectionTypes?.data ?? [])
      : InspectionCreateFormMeta(
          sites?.data ?? [],
          inspectionTypes?.data ?? [],
          assigneeCandidates ?? []
        );
  }, [
    isOptionsLoading,
    isEditMode,
    sites,
    inspectionTypes,
    assigneeCandidates,
  ]);

  const form = useEntitySyncedForm({
    formMeta,
    entity: fetchedInspection,
    toFormData: toFormDataV2,
  });
  const {setFieldValue} = form;

  // Single-option auto-seed (create only): a lone-option select shows its label but holds no
  // value until clicked, so a user with exactly one site/type would fail "is required".
  useEffect(() => {
    if (isCreateMode && sites?.data?.length === 1) {
      setFieldValue('siteId', String(sites.data[0].id));
    }
  }, [isCreateMode, sites, setFieldValue]);

  useEffect(() => {
    if (isCreateMode && inspectionTypes?.data?.length === 1) {
      setFieldValue('inspectionTypeId', String(inspectionTypes.data[0].id));
    }
  }, [isCreateMode, inspectionTypes, setFieldValue]);

  // The selected site is a live form value, not the loaded entity — watch it
  // reactively rather than reading a stale prop.
  const watchedSiteId = useWatch({
    control: form.methods.control,
    name: 'siteId',
  });
  const {data: watchedSite} = useGetSiteQuery(Number(watchedSiteId), {
    skip: !isCreateMode || !watchedSiteId,
  });
  const showAddressWarning =
    isCreateMode &&
    !!watchedSite &&
    !watchedSite.address?.trim() &&
    !watchedSite.city?.trim();

  const {goBack: handleCancel} = useBackNavigation(fallbackTo);

  const handleSave = async () => {
    setIsSavePending(true);

    try {
      const values = form.getFieldValues();
      let response: InspectionResponse;

      if (isCreateMode) {
        const model: InspectionRequest = {
          siteId: Number(values.siteId),
          inspectionTypeId: Number(values.inspectionTypeId),
          assigneeId: values.assigneeId ? Number(values.assigneeId) : undefined,
          dueAt: values.dueAt as unknown as Date | undefined,
        };
        response = await createInspection(model).unwrap();
        setAlert('Inspection was successfully created.', 'success');
      } else {
        const model: InspectionRequest =
          MapInspectionResponseToInspectionRequest({
            ...item,
            ...values,
            id: item?.id,
          } as InspectionResponse);
        response = await updateInspection(model).unwrap();
        setAlert('Inspection was successfully updated.', 'success');
      }

      navigate(
        isCreateMode ? PathUtility.InspectionsView(response.id) : fallbackTo,
        {replace: true}
      );
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsSavePending(false);
  };

  const handleValidateAndSave = async () => {
    const isValid = await form.validateForm();
    if (isValid) {
      void handleSave();
    }
  };

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  const pageTitle = isCreateMode ? 'New Inspection' : 'Edit Inspection';

  return (
    <Stack>
      <BackBar
        fallbackTo={fallbackTo}
        fallbackLabel={fallbackLabel}
        breadcrumbs={breadcrumbs}
      />

      <PageHeader>
        <PageHeader.Title icon={SidebarLinkInspections.icon}>
          {pageTitle}
        </PageHeader.Title>

        {isEditMode && (
          <PageHeader.Badges>
            <PageHeader.Badge color="yellow" variant="light">
              Editing
            </PageHeader.Badge>
          </PageHeader.Badges>
        )}

        <PageHeader.Actions>
          <PageHeader.Action variant="cancel" onClick={handleCancel}>
            Cancel
          </PageHeader.Action>
          <PageHeader.Action
            variant={isCreateMode ? 'create' : 'save'}
            onClick={handleValidateAndSave}
            loading={isSavePending}>
            {isCreateMode ? 'Create Inspection' : 'Save Changes'}
          </PageHeader.Action>
        </PageHeader.Actions>
      </PageHeader>

      <Box maw={640}>
        <ProactForm form={form}>
          <ProactFormGrid />
        </ProactForm>

        {showAddressWarning && (
          <InfoAlert mb="md">
            This site has no address on file — location confirmation will show
            as unconfirmed for the field inspector.
          </InfoAlert>
        )}
      </Box>

      <Group>
        <PageHeader.Action variant="cancel" onClick={handleCancel}>
          Cancel
        </PageHeader.Action>
        <PageHeader.Action
          variant={isCreateMode ? 'create' : 'save'}
          onClick={handleValidateAndSave}
          loading={isSavePending}>
          {isCreateMode ? 'Create Inspection' : 'Save Changes'}
        </PageHeader.Action>
      </Group>
    </Stack>
  );
};
